import { ACTIVITY_TEMPLATES, US_STATES, CA_PROVINCES, NEIGHBORS, ITU_PREFIXES, CQ_ZONES, ITU_ZONES, CONTEST_TEMPLATES, COMMON_NAMES, ARRL_SECTIONS, ARRL_PRECEDENCE, IARU_HQ_STATIONS, CWOPS_MEMBERS } from './data/qsoData';

// --- CALL SIGN UTILS ---

/**
 * Validates an amateur radio callsign format.
 * Standard format: 1-3 char prefix, 1 digit, 1-4 char suffix.
 * Also supports common portable/mobile suffixes.
 * @param {string} callSign - The callsign to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const isValidCallSign = (callSign) => {
    if (!callSign) return false;
    // Basic pattern: 1-3 alphanumeric prefix, 1 digit, 1-4 alphanumeric suffix
    // Plus optional /P, /M, /R, or /digits suffix
    const regex = /^[A-Z0-9]{1,3}\d[A-Z0-9]{1,4}(\/[A-Z0-9]+)?$/;
    return regex.test(callSign.toUpperCase());
};

/**
 * Detects the CQ zone from a US callsign based on the call area number
 * @param {string} callSign - The callsign to analyze
 * @returns {number|null} - The CQ zone number, or null if not determinable
 */
export const detectUSCQZone = (callSign) => {
    if (!callSign) return null;

    // Extract the call area number from US callsigns
    // Format examples: W0HT, K1ABC, N7XYZ, AA0ZZ, KA1XYZ
    const match = callSign.match(/[AKNW][A-Z]?(\d)/);
    if (!match) return null;

    const callArea = parseInt(match[1]);

    // Map call areas to CQ zones based on FCC call area boundaries
    // Zone 3: Upper Midwest, Rocky Mountains, Pacific Northwest
    if (callArea === 0 || callArea === 7 || callArea === 8 || callArea === 9) {
        return 3;
    }
    // Zone 4: Southeast
    else if (callArea === 4) {
        return 4;
    }
    // Zone 5: Northeast, Mid-Atlantic, Southwest, California
    else if (callArea === 1 || callArea === 2 || callArea === 3 || callArea === 5 || callArea === 6) {
        return 5;
    }

    return null;
};

export const detectCountry = (callSign) => {
    if (!callSign) return null;
    for (const [country, prefixes] of Object.entries(ITU_PREFIXES)) {
        if (country === 'WORLD') continue;
        for (const prefix of prefixes) {
            if (prefix.includes('-')) {
                const [start, end] = prefix.split('-');
                const subLength = start.length;
                const sub = callSign.substring(0, subLength);
                if (sub.length === subLength && sub >= start && sub <= end) return country;
            } else {
                if (callSign.startsWith(prefix)) return country;
            }
        }
    }
    return null;
};

export const generateCallSign = (region = 'US') => {
    const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const nums = "0123456789";
    const rand = (str) => str[Math.floor(Math.random() * str.length)];
    const randArr = (arr) => arr[Math.floor(Math.random() * arr.length)];

    const suffix = () => {
        const len = Math.floor(Math.random() * 3) + 1;
        let s = "";
        for (let i = 0; i < len; i++) s += rand(letters);
        return s;
    };

    if (region === 'US') {
        const type = Math.random();
        const numeral = rand(nums);
        if (type < 0.1) return "W" + numeral + rand(letters) + rand(letters);
        if (type < 0.3) return "K" + numeral + suffix();
        if (type < 0.5) return "N" + rand(letters) + numeral + rand(letters);
        if (type < 0.6) return "AA" + numeral + rand(letters); // Simplified AA
        if (type < 0.75) {
            // Generate K or W or N with 2 letters
            const p = rand("KWN");
            return p + rand(letters) + numeral + rand(letters);
        }
        // Generate AA-AL
        const second = "ABCDEFGHIJKL"[Math.floor(Math.random() * 12)];
        return "A" + second + numeral + rand(letters) + rand(letters);
    }

    // Russia Special Logic (Region constrained numbers)
    if (region === 'RU_EU') {
        const prefix = randArr(['R', 'RA', 'UA']);
        const num = rand("1234567");
        return prefix + num + suffix();
    }
    if (region === 'RU_AS') {
        const prefix = randArr(['R', 'RA', 'UA']);
        const num = rand("890");
        return prefix + num + suffix();
    }

    let prefixPool = ITU_PREFIXES[region];
    if (!prefixPool || region === 'WORLD') {
        prefixPool = [];
        Object.keys(ITU_PREFIXES).forEach(key => {
            if (key !== 'WORLD' && key !== 'US') {
                prefixPool = [...prefixPool, ...ITU_PREFIXES[key]];
            }
        });
    }

    let prefix = randArr(prefixPool);
    if (prefix.includes('-')) {
        const [start, end] = prefix.split('-');
        const base = start.substring(0, start.length - 1);
        const startChar = start.charCodeAt(start.length - 1);
        const endChar = end.charCodeAt(end.length - 1);
        const randomChar = String.fromCharCode(Math.floor(Math.random() * (endChar - startChar + 1)) + startChar);
        prefix = base + randomChar;
    }

    return prefix + rand(nums) + suffix();
};

// --- ACTIVITY GENERATOR ---
export const generateActivity = (type, role, userSettings) => {
    const templateKey = `${type}_${role}`;
    const templates = ACTIVITY_TEMPLATES[templateKey] || ACTIVITY_TEMPLATES['POTA_HUNTER'];
    const template = templates[Math.floor(Math.random() * templates.length)];
    const randArr = (arr) => arr[Math.floor(Math.random() * arr.length)];

    const userCountry = userSettings?.country || 'US';

    // MY CALL (The User)
    const myCall = userSettings?.callSign || generateCallSign(userCountry);

    // THEIR CALL (The "Other Station") - Weighted Distribution
    let targetRegion = 'WORLD';
    const roll = Math.random();

    if (roll < 0.7) {
        // 70% Chance: Same Country (Local)
        targetRegion = userCountry;
    } else if (roll < 0.9) {
        // 20% Chance: Regional Neighbor
        const neighbors = NEIGHBORS[userCountry] || ['WORLD'];
        targetRegion = randArr(neighbors);
    } else {
        // 10% Chance: Rare/World DX
        targetRegion = 'WORLD';
    }

    const theirCall = generateCallSign(targetRegion);

    // GENERATE EXTRAS
    const park = `K-${Math.floor(Math.random() * 8999) + 1000}`;
    const summit = `W${Math.floor(Math.random() * 7)}/CR-${Math.floor(Math.random() * 999).toString().padStart(3, '0')}`;

    const wwffPrefixes = ['K', 'VK', 'G', 'DL', 'F', 'I', 'SP'];
    const p = wwffPrefixes[Math.floor(Math.random() * wwffPrefixes.length)];
    const wwffRef = `${p}FF-${Math.floor(Math.random() * 9999).toString().padStart(4, '0')}`;

    let state = randArr(US_STATES);
    if (userSettings?.regionCode) {
        state = userSettings.regionCode;
    } else if (userSettings?.country === 'CA') {
        state = randArr(CA_PROVINCES);
    }

    return template
        .replace(/{MY_CALL}/g, myCall)
        .replace(/{THEIR_CALL}/g, theirCall)
        .replace(/{PARK}/g, park)
        .replace(/{SUMMIT}/g, summit)
        .replace(/{WWFF_REF}/g, wwffRef)
        .replace(/{STATE}/g, state);
};

// --- CONTEST GENERATOR ---
export const generateContest = (contestType, userSettings) => {
    const templateKey = contestType;
    const templates = CONTEST_TEMPLATES[templateKey] || CONTEST_TEMPLATES['CQWW_SO'];
    const template = templates[Math.floor(Math.random() * templates.length)];
    const randArr = (arr) => arr[Math.floor(Math.random() * arr.length)];

    const userCountry = userSettings?.country || 'US';

    // MY CALL (The User)
    const myCall = userSettings?.callSign || generateCallSign(userCountry);

    // THEIR CALL (The "Other Station") - Weighted Distribution 
    let targetRegion = 'WORLD';
    const roll = Math.random();

    if (roll < 0.4) {
        targetRegion = userCountry;  // 40% same country
    } else if (roll < 0.7) {
        const neighbors = NEIGHBORS[userCountry] || ['WORLD'];
        targetRegion = randArr(neighbors);  // 30% neighbors
    } else {
        targetRegion = 'WORLD';  // 30% DX
    }

    const theirCall = generateCallSign(targetRegion);

    // Contest-specific exchanges
    if (contestType === 'CQWPX_CW') {
        // WPX uses serial numbers (001-999)
        const mySerial = String(Math.floor(Math.random() * 999) + 1).padStart(3, '0');
        const theirSerial = String(Math.floor(Math.random() * 999) + 1).padStart(3, '0');

        return template
            .replace(/{MY_CALL}/g, myCall)
            .replace(/{THEIR_CALL}/g, theirCall)
            .replace(/{MY_SERIAL}/g, mySerial)
            .replace(/{THEIR_SERIAL}/g, theirSerial);
    } else if (contestType === 'CQWW_SO') {
        const myCountry = detectCountry(myCall) || 'US';
        const theirCountry = detectCountry(theirCall) || 'US';

        // Smart zone detection using detectCQZone for all supported countries
        let myZone;
        const detectedMyZone = detectCQZone(myCall, myCountry);
        if (detectedMyZone) {
            myZone = String(detectedMyZone).padStart(2, '0');
        } else {
            // Fallback to random selection from country's zones
            const myZones = CQ_ZONES[myCountry] || [5];
            myZone = String(myZones[Math.floor(Math.random() * myZones.length)]).padStart(2, '0');
        }

        // Smart zone detection for their callsign
        let theirZone;
        const detectedTheirZone = detectCQZone(theirCall, theirCountry);
        if (detectedTheirZone) {
            theirZone = String(detectedTheirZone).padStart(2, '0');
        } else {
            // Fallback to random selection
            const theirZones = CQ_ZONES[theirCountry] || [14];
            theirZone = String(theirZones[Math.floor(Math.random() * theirZones.length)]).padStart(2, '0');
        }

        return template
            .replace(/{MY_CALL}/g, myCall)
            .replace(/{THEIR_CALL}/g, theirCall)
            .replace(/{MY_ZONE}/g, myZone)
            .replace(/{THEIR_ZONE}/g, theirZone);
    } else if (contestType === 'CWT') {
        const myName = userSettings?.name || "ME";
        const myNum = userSettings?.cwops || Math.floor(Math.random() * 4000) + 1000;

        let finalTheirCall = theirCall;
        let theirName;
        let theirNum;

        // 30% chance to use a real CWops member
        if (Math.random() < 0.3 && CWOPS_MEMBERS && CWOPS_MEMBERS.length > 0) {
            const member = CWOPS_MEMBERS[Math.floor(Math.random() * CWOPS_MEMBERS.length)];
            finalTheirCall = member.call;
            theirName = member.name;
            theirNum = member.num;
        } else {
            const names = ["BOB", "JIM", "TOM", "DAN", "JOE", "AL", "ED"];
            theirName = names[Math.floor(Math.random() * names.length)];
            theirNum = Math.floor(Math.random() * 4000) + 1000;
        }

        return template
            .replace(/{MY_CALL}/g, myCall)
            .replace(/{THEIR_CALL}/g, finalTheirCall)
            .replace(/{MY_NAME}/g, myName)
            .replace(/{THEIR_NAME}/g, theirName)
            .replace(/{MY_CWOPS}/g, myNum)
            .replace(/{THEIR_CWOPS}/g, theirNum);
    } else if (contestType === 'IARU_HF') {
        // IARU Exchange: Signal Report + ITU Zone (or HQ)
        const myCountry = detectCountry(myCall) || 'US';
        const theirCountry = detectCountry(theirCall) || 'US';

        const myZones = ITU_ZONES[myCountry] || [8];
        const theirZones = ITU_ZONES[theirCountry] || [28];

        const myEx = myZones[Math.floor(Math.random() * myZones.length)];
        const theirEx = theirZones[Math.floor(Math.random() * theirZones.length)];

        return template
            .replace(/{MY_CALL}/g, myCall)
            .replace(/{THEIR_CALL}/g, theirCall)
            .replace(/{MY_EXCHANGE}/g, myEx)
            .replace(/{THEIR_EXCHANGE}/g, theirEx);
    } else if (contestType === 'ARRL_SS') {
        // ARRL Sweepstakes Exchange: Serial# Precedence Callsign Check Section
        const mySerial = String(Math.floor(Math.random() * 999) + 1);
        const theirSerial = String(Math.floor(Math.random() * 999) + 1);

        const myPrec = ARRL_PRECEDENCE[Math.floor(Math.random() * ARRL_PRECEDENCE.length)];
        const theirPrec = ARRL_PRECEDENCE[Math.floor(Math.random() * ARRL_PRECEDENCE.length)];

        // Check is last 2 digits of the year you were licensed (or year call was issued)
        const myCheck = String(Math.floor(Math.random() * 100)).padStart(2, '0');
        const theirCheck = String(Math.floor(Math.random() * 100)).padStart(2, '0');

        // Section is ARRL section
        const mySection = ARRL_SECTIONS[Math.floor(Math.random() * ARRL_SECTIONS.length)];
        const theirSection = ARRL_SECTIONS[Math.floor(Math.random() * ARRL_SECTIONS.length)];

        return template
            .replace(/{MY_CALL}/g, myCall)
            .replace(/{THEIR_CALL}/g, theirCall)
            .replace(/{MY_SERIAL}/g, mySerial)
            .replace(/{THEIR_SERIAL}/g, theirSerial)
            .replace(/{MY_PREC}/g, myPrec)
            .replace(/{THEIR_PREC}/g, theirPrec)
            .replace(/{MY_CHECK}/g, myCheck)
            .replace(/{THEIR_CHECK}/g, theirCheck)
            .replace(/{MY_SECTION}/g, mySection)
            .replace(/{THEIR_SECTION}/g, theirSection);
    } else {
        // Fallback for unknown types
        return template
            .replace(/{MY_CALL}/g, myCall)
            .replace(/{THEIR_CALL}/g, theirCall);
    }
};

/**
 * Detects the CQ zone from a callsign based on country-specific patterns
 * Supports US, Canada, Russia, Australia, and Brazil with geographic accuracy
 * @param {string} callSign - The callsign to analyze
 * @param {string} country - The country code (optional, will be detected if not provided)
 * @returns {number|null} - The CQ zone number, or null if not determinable
 */
export const detectCQZone = (callSign, country = null) => {
    if (!callSign) return null;

    // Detect country if not provided
    if (!country) {
        country = detectCountry(callSign);
    }

    // Try country-specific detection
    if (country === 'US') {
        return detectUSCQZone(callSign);
    }

    // --- CANADA ---
    if (country === 'CA') {
        const match = callSign.match(/V([AEOY])(\d)/);
        if (match) {
            const prefixChar = match[1];
            const callArea = parseInt(match[2]);

            // VO prefix is always Zone 2 (Newfoundland/Labrador)
            if (prefixChar === 'O') return 2;

            // Zone 2: Quebec, Northern territories
            if (callArea === 2 || callArea === 8) return 2;
            // Zone 3: Western Canada (BC, AB, SK)
            if (callArea === 5 || callArea === 6 || callArea === 7) return 3;
            // Zone 4: Central Canada (MB, ON)
            if (callArea === 3 || callArea === 4) return 4;
            // Zone 5: Maritime provinces
            if (callArea === 1 || callArea === 9) return 5;
        }
    }

    // --- RUSSIA ---
    if (country === 'RU_EU' || country === 'RU_AS' || country === 'RU') {
        const match = callSign.match(/[RU][A-Z]?(\d)/);
        if (match) {
            const callArea = parseInt(match[1]);

            // Zone 16: European Russia
            if (callArea >= 1 && callArea <= 7) return 16;
            // Zone 18: Asiatic Russia (simplified - could be 17-19, 23)
            if (callArea === 8 || callArea === 9 || callArea === 0) return 18;
        }
    }

    // --- AUSTRALIA ---
    if (country === 'AU') {
        const match = callSign.match(/VK(\d)/);
        if (match) {
            const state = parseInt(match[1]);

            // Zone 30: Eastern states (NSW, VIC, QLD, TAS, NT)
            if ((state >= 1 && state <= 4) || state === 7 || state === 8) return 30;
            // Zone 29: Western states (SA, WA)
            if (state === 5 || state === 6) return 29;
        }
    }

    // --- BRAZIL ---
    if (country === 'BR') {
        const match = callSign.match(/P[PQRSTUVWXY](\d)/);
        if (match) {
            const area = parseInt(match[1]);

            // Zone 11: Northern/Northeastern Brazil
            if ((area >= 1 && area <= 4) || area === 6 || area === 7) return 11;
            // Zone 13: Southern Brazil
            if (area === 5 || area === 8 || area === 0) return 13;
        }
    }

    return null;
};

/**
 * Detects the user's country and region using ipapi.co
 * @returns {Promise<{country: string, region: string}|null>} Location object
 */
export const detectLocationFromIP = async () => {
    try {
        const response = await fetch('https://ipapi.co/json/');
        if (!response.ok) throw new Error('Network response was not ok');
        const data = await response.json();
        return {
            country: data.country_code,
            region: data.region_code
        };
    } catch (error) {
        console.warn('Error detecting location:', error);
        return null;
    }
};

